import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/api/tickets.api';
import { usersApi } from '@/api/users.api';
import { Modal } from '@/components/ui/Modal';
import { Field } from '@/components/ui/Field';
import { ApiError } from '@/lib/errors';
import { viewTypeLabel } from '@/lib/labels';
import { useToasts } from '@/context/ToastContext';
import { invalidateAfterTicketAction } from '@/lib/queryInvalidation';
import { useTagTeammates } from './useTagTeammates';

interface Props {
  ticketId: string;
  open: boolean;
  onClose: () => void;
  /** V4.8 — current assignees so the modal can pre-populate dropdowns. */
  currentOwnerId?: string | null;
  currentSolverId?: string | null;
  currentCommunicatorId?: string | null;
  currentInvestigatorId?: string | null;
}

/**
 * V4.8 — Reassign modal now lets the user change ANY of the four
 * ticket roles (Owner, Solver, Communicator, Investigator) in a single
 * submit. Each dropdown is independent and pre-populated with the
 * current assignee. Submitting only sends the fields the user actually
 * changed — minimising backend work and keeping audit trail clean.
 *
 * UX rules:
 *  - At least ONE role must be changed (else Save is disabled).
 *  - "— Unchanged —" option appears at the top of every dropdown so
 *    admins can leave a role alone without a separate toggle.
 *  - On success the modal toasts a summary of which roles changed, and
 *    the parent page invalidates ticket query → Team panel refreshes
 *    automatically.
 */
export function ReassignModal({
  ticketId,
  open,
  onClose,
  currentOwnerId,
  currentSolverId,
  currentCommunicatorId,
  currentInvestigatorId
}: Props) {
  const [ownerId, setOwnerId] = useState<string>('');
  const [solverId, setSolverId] = useState<string>('');
  const [communicatorId, setCommunicatorId] = useState<string>('');
  const [investigatorId, setInvestigatorId] = useState<string>('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const tag = useTagTeammates();

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    enabled: open
  });

  // Initialise each dropdown to the current assignee whenever the modal
  // opens or the current IDs change. Empty string ('') means "no change".
  useEffect(() => {
    if (!open) return;
    setOwnerId(currentOwnerId ?? '');
    setSolverId(currentSolverId ?? '');
    setCommunicatorId(currentCommunicatorId ?? '');
    setInvestigatorId(currentInvestigatorId ?? '');
    setReason('');
    setError(null);
  }, [open, currentOwnerId, currentSolverId, currentCommunicatorId, currentInvestigatorId]);

  // Determine which fields actually changed compared to baseline.
  const diff = useMemo(() => {
    const out: {
      new_owner_id?: string;
      new_solver_id?: string;
      new_communicator_id?: string;
      new_investigator_id?: string;
    } = {};
    if (ownerId && ownerId !== (currentOwnerId ?? '')) out.new_owner_id = ownerId;
    if (solverId && solverId !== (currentSolverId ?? '')) out.new_solver_id = solverId;
    if (communicatorId && communicatorId !== (currentCommunicatorId ?? ''))
      out.new_communicator_id = communicatorId;
    if (investigatorId && investigatorId !== (currentInvestigatorId ?? ''))
      out.new_investigator_id = investigatorId;
    return out;
  }, [ownerId, solverId, communicatorId, investigatorId, currentOwnerId, currentSolverId, currentCommunicatorId, currentInvestigatorId]);

  const changedRoles = Object.keys(diff).map((k) => k.replace(/^new_/, '').replace(/_id$/, ''));

  const mutation = useMutation({
    mutationFn: async () => {
      // Only call reassign if a role actually changed — the Tag section can
      // be used on its own (tag-only) without touching any assignment.
      if (Object.keys(diff).length > 0) {
        await ticketsApi.reassign(ticketId, {
          ...diff,
          reason: reason || undefined
        });
      }
      if (tag.hasContent) await ticketsApi.tag(ticketId, tag.payload());
    },
    onSuccess: () => {
      if (changedRoles.length > 0) {
        const verb = changedRoles.length === 1 ? changedRoles[0] : `${changedRoles.length} roles`;
        push({ kind: 'success', title: `Reassigned ${verb}` });
      } else {
        push({ kind: 'success', title: 'Teammates tagged' });
      }
      // V4.9.3 — invalidate ALL views so the header pills + every list
      // shows the new solver/communicator/owner instantly. No more
      // "saved but the pill at the top still shows old name" lag.
      invalidateAfterTicketAction(queryClient, ticketId);
      tag.reset();
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.problem?.detail ?? err.message : 'Failed to reassign');
    }
  });

  const activeUsers = useMemo(
    () => (usersQuery.data ?? []).filter((u) => u.active),
    [usersQuery.data]
  );

  const userOption = (u: { id: string; name: string; alias: string; view_type: string }) => {
    const showAlias = u.alias && u.alias !== u.name;
    const aliasPart = showAlias ? ` (@${u.alias})` : '';
    return (
      <option key={u.id} value={u.id}>
        {u.name}
        {aliasPart} — {viewTypeLabel(u.view_type as any)}
      </option>
    );
  };

  const canSave = (changedRoles.length > 0 || tag.hasContent) && !mutation.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reassign team"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSave}
            onClick={() => mutation.mutate()}
            title={
              changedRoles.length === 0
                ? tag.hasContent
                  ? 'Will tag the selected teammates'
                  : 'Change a role or tag a teammate to enable Save'
                : `Will update: ${changedRoles.join(', ')}`
            }
          >
            {mutation.isPending
              ? 'Saving...'
              : changedRoles.length > 0
              ? `Save (${changedRoles.length} change${changedRoles.length > 1 ? 's' : ''})`
              : 'Save'}
          </button>
        </>
      }
    >
      <div className="text-muted text-small" style={{ marginBottom: 12 }}>
        Update any combination of roles. Leave a dropdown unchanged to keep its current assignee.
      </div>

      <Field label="Owner">
        <select
          className="form-select"
          value={ownerId}
          onChange={(e) => setOwnerId(e.target.value)}
          disabled={usersQuery.isLoading}
        >
          <option value="">— Keep current —</option>
          {activeUsers.map(userOption)}
        </select>
      </Field>

      <Field label="Solver">
        <select
          className="form-select"
          value={solverId}
          onChange={(e) => setSolverId(e.target.value)}
          disabled={usersQuery.isLoading}
        >
          <option value="">— Keep current —</option>
          {activeUsers.map(userOption)}
        </select>
      </Field>

      <Field label="Communicator">
        <select
          className="form-select"
          value={communicatorId}
          onChange={(e) => setCommunicatorId(e.target.value)}
          disabled={usersQuery.isLoading}
        >
          <option value="">— Keep current —</option>
          {activeUsers.map(userOption)}
        </select>
      </Field>

      <Field label="Investigator">
        <select
          className="form-select"
          value={investigatorId}
          onChange={(e) => setInvestigatorId(e.target.value)}
          disabled={usersQuery.isLoading}
        >
          <option value="">— Keep current —</option>
          {activeUsers.map(userOption)}
        </select>
      </Field>

      <Field label="Reason (optional)">
        <textarea
          className="form-textarea"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. 'Anitha out today, handing to Manusha for the week.'"
        />
      </Field>

      {tag.render()}
      {error ? <div className="form-error">{error}</div> : null}
    </Modal>
  );
}
